import { useAuth } from '@agentnet/auth';
import { DataTable } from '@fluentsms/agentnet-web-components';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';
import { CellClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { getJacketFields, getRateTypes } from 'api/jacket-api';
import { FirmAccount } from 'api/profile/types';
import { Endorsement, PolicyFieldsModel, RateTypes } from 'features/jackets/types';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetDivider } from 'ui-kit/components/dividers';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { DeleteOutlineIcon } from 'ui-kit/icons/DeleteOutlineIcon';
import { AgentNetDropdownSelector, CurrencyField } from 'ui-kit/inputs';
import { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompletePolicySearch, { searchType } from 'ui-kit/inputs/AutocompletePolicySearch/AutocompletePolicySearch';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { formatDate } from 'utilities/utilities';
import { FieldValidationError } from 'utilities/validation/validation';
import { statCodeStateList } from './createNewFileConstants';
import { CreateNewFileJacketData, CreateNewFileJacketEndorsementData } from './CreateNewFileDrawer';
import { PolicyInventoryType } from './types';
import { UnderlinedCurrencyEditor, UnderlinedCurrencyEditorStyle } from './UnderlinedCurrencyEditor';

interface CreateNewFileJacketProps {
  key: number;
  account: FirmAccount | null;
  stateCode: string;
  countyCode: string;
  policies: PolicyInventoryType[];
  selectedPolicies: string[];
  endorsements: CreateNewFileJacketEndorsementData[];
  jacket: CreateNewFileJacketData;
  onChange: (key: number, jacket: CreateNewFileJacketData | null) => void;
  validationErrors: FieldValidationError[] | undefined;
}

function findPolicy(
  input: string | undefined,
  stateCode: string,
  policies: PolicyInventoryType[],
): PolicyInventoryType | null {
  if (!input) {
    return null;
  }
  for (const policy of policies) {
    if (!policy.stateList.includes(stateCode)) {
      continue;
    }
    const foundPolicy = policy.policyNumbers.find((policyNumber) => policyNumber === input);
    if (foundPolicy) {
      return policy;
    }
  }
  return null;
}

function getSearchEndorsements(
  endorsements: CreateNewFileJacketEndorsementData[],
  selectedEndorsements: CreateNewFileJacketEndorsementData[],
): searchType[] {
  const searchTypes: searchType[] = [];
  for (const endorsement of endorsements) {
    if (selectedEndorsements.find((e) => e.endorsementFormNumber === endorsement.endorsementFormNumber)) {
      continue;
    }
    searchTypes.push({
      name: endorsement.endorsementFormNumber,
      value: endorsement.endorsementFormNumber,
      display: endorsement.endorsementName,
      isActive: true,
    });
  }
  return searchTypes;
}

function getSearchPolicies(
  input: string | undefined,
  stateCode: string,
  policies: PolicyInventoryType[],
  selectedPolicies: string[],
): searchType[] {
  const searchTypes: searchType[] = [];
  searchTypes.push({
    name: 'No Record Of Policy',
    value: 'No Record Of Policy',
    display: 'No Record Of Policy',
    isActive: true,
  });
  const uniqueSet = new Set<string>();
  for (const policy of policies) {
    if (!policy.stateList.includes(stateCode)) {
      continue;
    }
    for (const policyNumber of policy.policyNumbers) {
      if (selectedPolicies.includes(policyNumber) || uniqueSet.has(policyNumber)) {
        continue;
      }
      if (!input || policyNumber.includes(input)) {
        searchTypes.push({
          name: policyNumber,
          value: policyNumber,
          display: policy.itemDescription,
          isActive: true,
        });
        uniqueSet.add(policyNumber);
      }
    }
  }
  return searchTypes;
}

function getMaxDateString(): string | undefined {
  const d = new Date();
  return (
    (d.getMonth() + 1).toString().padStart(2, '0') +
    '/' +
    d.getDate().toString().padStart(2, '0') +
    '/' +
    d.getFullYear()
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    box: {
      border: '1px solid #c4c4c4',
      borderRadius: '6px',
      width: '97.5%',
      marginLeft: 10,
      marginBottom: 10,
      paddingBottom: 10,
    },
    jacketContainer: {
      marginLeft: 10,
      marginTop: 10,
      width: '98%',
    },
    jacketBox: {
      backgroundColor: '#f3fbff',
      fontSize: 14,
      paddingTop: 10,
      paddingLeft: 20,
      paddingBottom: 10,
      borderBottom: '1px solid #c4c4c4',
      borderRadius: '6px',
    },
    jacketLabel: {
      fontWeight: 500,
    },
    jacketPolicyLabel: {
      fontSize: 12,
      marginTop: 5,
    },
    trashcan: {
      margin: 'auto',
      flexBasis: 0,
    },
  }),
);

export function CreateNewFileJacket({
  key,
  account,
  stateCode,
  countyCode,
  policies,
  selectedPolicies,
  endorsements,
  jacket,
  onChange,
  validationErrors,
}: CreateNewFileJacketProps): JSX.Element {
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const [searchPolicyNumberInput, setSearchPolicyNumberInput] = useState<string>();
  const [policy, setPolicy] = useState<PolicyInventoryType | null>(null);
  const [policyDate, setPolicyDate] = useState<string>();
  const [rateTypeId, setRateTypeId] = useState<number>(0);
  const [rateTypesSelections, setRateTypesSelections] = useState<SelectOption[]>([]);
  const [statCodesSelections, setStatCodesSelections] = useState<SelectOption[]>([]);
  const [addEndorsementDialogOpen, setAddEndorsementDialogOpen] = useState(false);
  const [addEndorsement, setAddEndorsement] = useState<Endorsement | null>(null);
  const [gridEndorsements, setGridEndorsements] = useState<CreateNewFileJacketEndorsementData[]>([]);
  const classes = useStyles();

  const statCodeRequired = statCodeStateList.includes(stateCode);

  const endorsementsColumnConfig: ColDef<CreateNewFileJacketEndorsementData>[] = [
    {
      colId: 'endorsementColumn',
      headerName: 'Endorsement',
      field: 'endorsementFormNumber',
      maxWidth: 144,
    },
    {
      colId: 'descriptionColumn',
      headerName: 'Description',
      field: 'endorsementName',
      minWidth: 175,
      maxWidth: 175,
    },
    {
      headerName: statCodeRequired ? 'Stat Code' : '',
      maxWidth: 150,
      minWidth: 150,
      field: 'statCode',
      cellEditor: 'agSelectCellEditor',
      cellRenderer: (params: ICellRendererParams<CreateNewFileJacketEndorsementData>) => {
        if (!statCodeRequired) {
          return <></>;
        }
        const value = jacket.endorsements.find((endorsement) => endorsement.id === params.data?.id);
        return (
          <AgentNetDropdownSelector
            label=""
            name="statCodes"
            options={statCodesSelections}
            value={value?.statCode ?? ''}
            disabled={!statCodeStateList.includes(stateCode) || statCodesSelections.length === 0}
            menuOption={(statCode: string) => {
              if (value) {
                value.statCode = statCode;
                setGridEndorsements([...jacket.endorsements]);
              }
            }}
          />
        );
      },
      suppressHeaderMenuButton: true,
    },
    {
      headerName: 'Gross',
      maxWidth: 115,
      minWidth: 115,
      field: 'grossInput',
      cellEditor: UnderlinedCurrencyEditor,
      cellRenderer: (params: ICellRendererParams<CreateNewFileJacketEndorsementData>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div style={UnderlinedCurrencyEditorStyle}>$0.00</div>;
        }
        params.data.gross = Number(numericValue.toFixed(2));
        const twoPlaces = numericValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <div style={UnderlinedCurrencyEditorStyle}>{formatted}</div>;
      },
      onCellClicked: (params) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'grossInput',
        });
      },
      editable: true,
    },
    {
      headerName: 'Net',
      maxWidth: 121,
      minWidth: 121,
      field: 'netInput',
      cellEditor: UnderlinedCurrencyEditor,
      cellRenderer: (params: ICellRendererParams<CreateNewFileJacketEndorsementData>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div style={UnderlinedCurrencyEditorStyle}>$0.00</div>;
        }
        params.data.net = Number(numericValue.toFixed(2));
        const twoPlaces = numericValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <div style={UnderlinedCurrencyEditorStyle}>{formatted}</div>;
      },
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'netInput',
        });
      },
      editable: true,
    },
    {
      headerName: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressMenu: true,
      floatingFilter: false,
      sortable: false,
      width: 60,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      pinned: 'right',
      cellRenderer: DeleteOutlineIcon,
      onCellClicked: (params) => {
        const { data } = params;
        const foundIndex = jacket.endorsements.findIndex(
          (e) => e.endorsementFormNumber === data?.endorsementFormNumber,
        );
        if (foundIndex > -1) {
          jacket.endorsements.splice(foundIndex, 1);
          setGridEndorsements([...jacket.endorsements]);
          onChange(key, jacket);
        }
      },
    },
  ];

  const getJacketFieldsApi = async (): Promise<PolicyFieldsModel> => {
    const token = await getAccessToken();
    return await getJacketFields(policy?.itemNo ?? '0', Number(account?.accountNumber), stateCode, countyCode, token);
  };

  const { execute: executeGetJacketFields, value: getJacketFieldsValue } = useAsync<PolicyFieldsModel>(
    getJacketFieldsApi,
    false,
  );

  const getRateTypesApi = async (): Promise<RateTypes[]> => {
    if (!policy) {
      return [{ rateTypeId: '1', rateTypeName: 'Basic' }];
    }
    const token = await getAccessToken();
    return await getRateTypes(
      policy?.itemNo ?? '0',
      stateCode,
      jacket.policyDate,
      false,
      countyCode,
      Number(account?.accountNumber),
      token,
    );
  };

  const {
    execute: executeGetRateTypes,
    status: getRateTypesStatus,
    errors: getRateTypesErrors,
    value: getRateTypesValue,
  } = useAsync<RateTypes[]>(getRateTypesApi, false);

  const renderAddEndorsementDialog = () => {
    return (
      addEndorsementDialogOpen && (
        <AgentNetConfirmationDialog
          open={addEndorsementDialogOpen}
          dialogTitle="Add Endorsement"
          dialogTextHTML={
            <AgentNetDropdownSelector
              label={'Endorsement'}
              name={'Endorsement'}
              qaAttribute={'Endorsement'}
              value={addEndorsement?.endorsementFormNumber}
              menuOption={(endorsement) => {
                const found = endorsements.find((e) => e.endorsementFormNumber === endorsement);
                if (found) {
                  setAddEndorsement(found);
                }
              }}
              options={getSearchEndorsements(endorsements, jacket.endorsements)}
              required
            />
          }
          dialogBtnContent="Add"
          secondaryActionBtnContent="Cancel"
          onDismissAction={() => setAddEndorsementDialogOpen(false)}
          onConfirm={() => {
            if (addEndorsement) {
              jacket.endorsements.push({
                ...addEndorsement,
                id: (Math.random() + 1).toString(36).substring(7),
                gross: 0,
                net: 0,
              });
              setGridEndorsements([...jacket.endorsements]);
              onChange(key, jacket);
              setAddEndorsement(null);
            }
            setAddEndorsementDialogOpen(false);
          }}
          disabled={false}
          disableBackDropClick
        />
      )
    );
  };

  useEffect(() => {
    if (policy && stateCode && countyCode) {
      executeGetJacketFields();
    }
  }, [policy, stateCode, countyCode]);

  useEffect(() => {
    const rateTypeIdString = rateTypeId.toString();
    if (getJacketFieldsValue) {
      setStatCodesSelections(
        getJacketFieldsValue.policyFields
          .filter((policyField) => policyField.name === 'StatCode')
          .flatMap((statCode) => {
            return statCode.options
              .filter((option) => option.rateTypeValue === rateTypeIdString)
              .map((option) => {
                return {
                  value: option.value,
                  name: option.text,
                };
              });
          }),
      );
    }
  }, [getJacketFieldsValue, rateTypeId]);

  useEffect(() => {
    if (policyDate) {
      executeGetRateTypes();
    }
  }, [policy, policyDate, countyCode]);

  useEffect(() => {
    if (getRateTypesStatus === 'success' && getRateTypesValue) {
      setRateTypesSelections(
        getRateTypesValue.map((rateType) => {
          return {
            value: rateType.rateTypeId,
            name: rateType.rateTypeName,
          };
        }),
      );
    } else if (getRateTypesStatus === 'error') {
      console.error(getRateTypesErrors);
      addGlobalMsg({
        message: 'There was an error while retrieving Rate Types.',
        type: 'error',
      });
      setRateTypesSelections([{ value: '1', name: 'Basic' }]);
    }
  }, [getRateTypesStatus]);

  return (
    <>
      <Box className={classes.box}>
        {policy && (
          <>
            <Grid container className={classes.jacketBox}>
              <Grid item sm={8}>
                <div>
                  <div className={classes.jacketLabel}>{policy?.itemDescription}</div>
                  <div className={classes.jacketPolicyLabel}>Policy No: {jacket.policyNumber}</div>
                </div>
              </Grid>
              <Grid item sm={3} />
              <Grid item sm={1} className={classes.trashcan}>
                <div
                  onClick={() => {
                    onChange(key, null);
                  }}
                >
                  <DeleteOutlineIcon />
                </div>
              </Grid>
            </Grid>
          </>
        )}
        <Grid key={key} container spacing={2} className={classes.jacketContainer}>
          <Grid item sm={6}>
            <AutocompletePolicySearch
              label="Policy Number"
              name="policyNumberSearchInput"
              hideSearchIcon
              onChange={(policyNumber) => {
                const input = policyNumber.target?.value;
                setSearchPolicyNumberInput(input);

                if (typeof policyNumber === 'string') {
                  const foundPolicy = findPolicy(policyNumber, stateCode, policies);
                  if (foundPolicy != policy) {
                    setPolicy(foundPolicy);
                    jacket.policyNumber = policyNumber;
                    onChange(key, jacket);
                  }
                } else {
                  setPolicy(null);
                  jacket.policyNumber = '';
                  onChange(key, jacket);
                }
              }}
              options={getSearchPolicies(searchPolicyNumberInput, stateCode, policies, selectedPolicies)}
              required
              showValidation
              error={validationErrors?.some((error) => error.field === 'policyNumber')}
              errs={validationErrors}
              helperText={
                validationErrors?.some((error) => error.field === 'policyNumber') && 'Policy Number is required'
              }
            />
          </Grid>
          <Grid item sm={6}></Grid>
          <Grid item sm={3}>
            <CurrencyField
              onBlur={(event) => {
                let liabilityAmount = 0;
                if (event.target.value) {
                  liabilityAmount = Number(event.target.value.replaceAll(',', ''));
                }
                if (liabilityAmount != jacket.liabilityAmount) {
                  jacket.liabilityAmount = liabilityAmount;
                  onChange(key, jacket);
                }
              }}
              fullWidth
              label={'Liability Amount'}
              required
              error={validationErrors?.some((error) => error.field === 'liabilityAmount')}
              errs={validationErrors}
              helperText={
                validationErrors?.some((error) => error.field === 'liabilityAmount') && 'Liability Amount is required'
              }
            />
          </Grid>
          <Grid item sm={3}>
            <DateFieldString
              fullWidth
              value={policyDate}
              maxDate={getMaxDateString()}
              label={'Policy Date'}
              onChange={(policyDate) => {
                let formattedPolicyDate = policyDate ? formatDate(policyDate || '') || '' : '';
                if (new Date(formattedPolicyDate) > new Date()) {
                  formattedPolicyDate = '';
                }
                setPolicyDate(formattedPolicyDate);
                jacket.policyDate = formattedPolicyDate;
                onChange(key, jacket);
              }}
              required
              showValidation
              error={validationErrors?.some((error) => error.field === 'policyDate')}
              errs={validationErrors}
            />
          </Grid>
          <Grid item sm={3}>
            <CurrencyField
              onBlur={(event) => {
                let reportedGross = 0;
                if (event.target.value) {
                  reportedGross = Number(event.target.value.replaceAll(',', ''));
                }
                if (reportedGross != jacket.reportedGross) {
                  jacket.reportedGross = reportedGross;
                  onChange(key, jacket);
                }
              }}
              fullWidth
              label={'Reported Gross'}
              required
              error={validationErrors?.some((error) => error.field === 'reportedGross')}
              errs={validationErrors}
              helperText={
                validationErrors?.some((error) => error.field === 'reportedGross') && 'Reported Gross is required'
              }
            />
          </Grid>
          <Grid item sm={3}>
            <CurrencyField
              onBlur={(event) => {
                let reportedNet = 0;
                if (event.target.value) {
                  reportedNet = Number(event.target.value.replaceAll(',', ''));
                }
                if (reportedNet != jacket.reportedNet) {
                  jacket.reportedNet = reportedNet;
                  onChange(key, jacket);
                }
              }}
              fullWidth
              label={'Reported Net'}
              required
              error={validationErrors?.some((error) => error.field === 'reportedNet')}
              errs={validationErrors}
              helperText={
                validationErrors?.some((error) => error.field === 'reportedNet') && 'Reported Net is required'
              }
            />
          </Grid>
          <Grid item sm={3}>
            <AgentNetDropdownSelector
              label={'Rate Type'}
              name={'Rate Type'}
              qaAttribute={'RateType'}
              options={rateTypesSelections}
              disabled={rateTypesSelections.length === 0}
              value={jacket.rateTypeId}
              menuOption={(rateTypeId) => {
                jacket.rateTypeId = rateTypeId;
                setRateTypeId(rateTypeId);
                onChange(key, jacket);
              }}
              required
              error={validationErrors?.some((error) => error.field === 'rateTypeId')}
              errs={validationErrors}
            />
          </Grid>
          <Grid item sm={3}>
            {statCodeRequired && (
              <AgentNetDropdownSelector
                label={'Stat Code'}
                name={'Stat Code'}
                qaAttribute={'StatCode'}
                options={statCodesSelections}
                value={jacket.statCode}
                menuOption={(statCode) => {
                  jacket.statCode = statCode;
                  onChange(key, jacket);
                }}
                required={statCodeRequired}
                error={validationErrors?.some((error) => error.field === 'statCode')}
                errs={validationErrors}
              />
            )}
          </Grid>
          <Grid item sm={3}>
            <CurrencyField
              onBlur={(event) => {
                let priorPolicyAmount = 0;
                if (event.target.value) {
                  priorPolicyAmount = Number(event.target.value.replaceAll(',', ''));
                }
                if (priorPolicyAmount !== jacket.priorPolicyAmount) {
                  jacket.priorPolicyAmount = priorPolicyAmount;
                  onChange(key, jacket);
                }
              }}
              fullWidth
              label={'Prior Liability Amount'}
            />
          </Grid>
          <Grid item sm={3}>
            <DateFieldString
              fullWidth
              maxDate={getMaxDateString()}
              label={'Prior Policy Date'}
              value={jacket.priorPolicyDate}
              onChange={(priorPolicyDate) => {
                let formattedPriorPolicyDate = priorPolicyDate ? formatDate(priorPolicyDate || '') || '' : '';
                if (new Date(formattedPriorPolicyDate) > new Date()) {
                  formattedPriorPolicyDate = '';
                }
                if (formattedPriorPolicyDate !== jacket.policyDate) {
                  jacket.priorPolicyDate = formattedPriorPolicyDate;
                  onChange(key, jacket);
                }
              }}
            />
          </Grid>
          <Grid item sm={6}>
            <AgentNetDivider typoVariant="h3" title={'Endorsements'} disablePadding />
          </Grid>
          <Grid item sm={3} />
          <Grid item sm={3}>
            <div>
              <AgentNetButton
                color="primary"
                size="small"
                variant="contained"
                onClick={() => {
                  setAddEndorsementDialogOpen(true);
                }}
                disabled={false}
                plusIcon
              >
                Add Endorsements
              </AgentNetButton>
            </div>
          </Grid>
          {gridEndorsements.length > 0 ? (
            <div style={{ height: 200, width: '100%' }}>
              <DataTable
                rowData={gridEndorsements}
                columnDefs={endorsementsColumnConfig}
                onCellValueChanged={(params) => {
                  const { data } = params;
                  const foundIndex = jacket.endorsements.findIndex(
                    (e) => e.endorsementFormNumber === data.endorsementFormNumber,
                  );
                  if (foundIndex > -1) {
                    jacket.endorsements[foundIndex] = data;
                    onChange(key, jacket);
                  }
                }}
                animateRows={false}
                noRowsOverlayComponent={'customOverlayNoRows'}
                stopEditingWhenCellsLoseFocus={true}
                rowHeight={40}
                suppressRowHoverHighlight={true}
                columnHoverHighlight={false}
                rowClass=""
                rowStyle={{ background: 'none' }}
              />
            </div>
          ) : null}
        </Grid>
      </Box>
      {renderAddEndorsementDialog()}
    </>
  );
}
